<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useFilterSummary } from "../../composables/Table/useFilterSummary";
import { useTableFilters } from "../../composables/Table/useTableFilters";
import VDropdown from "../Dropdown/VDropdown.vue";
import VTableFilterDropdown from "./VTableFilterDropdown.vue";
import VTableToolbarButton from "./VTableToolbarButton.vue";

defineOptions({
  name: "VTableQuickFilter",
});

/**
 * A filter as its own button in the toolbar, with the value in the button ("Kosten voor: Eigenaar"). For the one to
 * three fixed filters of a list; more filters belong in the filter panel.
 */
const { field } = defineProps<{
  field: string
}>();

const { t } = useI18n();
const { getConfig, getFilterForField, optionsOf, loadOptions } = useTableFilters();
const { summarize } = useFilterSummary();
const wrapperRef = ref<HTMLElement | null>(null);
const open = ref(false);

const config = computed(() => {
  const found = getConfig(field);
  if (!found) {
    throw new Error(`VTableQuickFilter: no filterable field "${field}" was provided.`);
  }
  return found;
});

onMounted(() => {
  // The value in the button needs the option names.
  void loadOptions(field).catch(() => {});
});

const active = computed(() => !!getFilterForField(field));
const text = computed(() => t("filters.quick_value", {
  label: config.value.label,
  value: summarize(config.value, getFilterForField(field), optionsOf(field)),
}));

/** Closes the popover and puts focus back on its button, so Escape works from the button and from the panel. */
function closeAndRefocus() {
  if (!open.value) {
    return;
  }
  open.value = false;
  void nextTick(() => wrapperRef.value?.querySelector<HTMLElement>("button")?.focus());
}

onClickOutside(wrapperRef, () => {
  open.value = false;
}, { ignore: [".v-popper__popper"] });
</script>

<template>
  <div ref="wrapperRef" class="inline-flex shrink-0" @keydown.esc="closeAndRefocus">
    <VDropdown
      theme="verbleif-popper"
      placement="bottom-start"
      :distance="6"
      :shown="open"
      :triggers="[]"
      :auto-hide="false"
      @hide="open = false"
    >
      <VTableToolbarButton
        icon="funnel"
        :label="text"
        :active="active"
        :open="open"
        haspopup="dialog"
        :data-testid="`table-quick-filter-${field}`"
        @click="open = !open"
      >
        {{ text }}
      </VTableToolbarButton>
      <template #popper>
        <VTableFilterDropdown v-if="open" :field="field" @close="closeAndRefocus" />
      </template>
    </VDropdown>
  </div>
</template>
